/**
 * ChatbotWidget — bulle flottante bas-droite + modal de chat.
 *
 * Comportement :
 *   • Bulle ronde verte avec icône message en bas-droite (toutes les pages)
 *   • Au clic → ouvre un panneau de chat (380x560 px) avec historique
 *   • Persiste l'historique en sessionStorage (par session navigateur)
 *   • Affiche typing dots pendant que Gemini répond
 *   • Boutons rapides (suggestions) sous chaque réponse du bot
 *   • À la fin, si le visiteur clique "Je veux être rappelé" → mini-form lead
 *
 * Backend :
 *   POST /api/chatbot/message  (envoi message + historique)
 *   POST /api/chatbot/lead     (capture lead final)
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Sparkles, User, Loader2 } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "em_chatbot_session";
const STORAGE_HISTORY_KEY = "em_chatbot_history";

const WELCOME_MESSAGES = [
    {
        role: "assistant",
        content:
            "Bonjour, je suis Emma 👋 — l'assistante d'Energy Market.\n\nJe peux vous aider à comparer vos offres énergie, vérifier vos aides État (1 € isolation, PAC, MaPrimeRénov'…) ou demander un rappel par un conseiller.\n\nQue puis-je faire pour vous ?",
    },
];

const INITIAL_SUGGESTIONS = [
    "Comment ça marche ?",
    "Suis-je éligible au 1 € ?",
    "Recevoir un devis",
];

export default function ChatbotWidget() {
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState(WELCOME_MESSAGES);
    const [suggestions, setSuggestions] = useState(INITIAL_SUGGESTIONS);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [leadFormOpen, setLeadFormOpen] = useState(false);
    const [lead, setLead] = useState({ full_name: "", email: "", phone: "" });
    const [leadSent, setLeadSent] = useState(false);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    // ─── Restaure session/historique au mount ───
    useEffect(() => {
        try {
            const sid = sessionStorage.getItem(STORAGE_KEY);
            if (sid) setSessionId(sid);
            const histRaw = sessionStorage.getItem(STORAGE_HISTORY_KEY);
            if (histRaw) {
                const parsed = JSON.parse(histRaw);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setMessages(parsed);
                }
            }
        } catch {}
    }, []);

    // ─── Persiste l'historique en sessionStorage ───
    useEffect(() => {
        try {
            sessionStorage.setItem(STORAGE_HISTORY_KEY, JSON.stringify(messages));
        } catch {}
    }, [messages]);

    // ─── Auto-scroll en bas quand un nouveau message arrive ou panel ouvert ───
    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open, sending]);

    // ─── Focus input à l'ouverture ───
    useEffect(() => {
        if (open && !leadFormOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 200);
        }
    }, [open, leadFormOpen]);

    // ─── Envoie un message ───
    const sendMessage = useCallback(
        async (text) => {
            const trimmed = (text || "").trim();
            if (!trimmed || sending) return;
            const newMessages = [...messages, { role: "user", content: trimmed }];
            setMessages(newMessages);
            setInput("");
            setSending(true);
            try {
                const { data } = await api.post("/chatbot/message", {
                    session_id: sessionId,
                    messages: newMessages.filter((m) => m.role !== "assistant" || m !== WELCOME_MESSAGES[0]),
                });
                if (data.session_id && !sessionId) {
                    setSessionId(data.session_id);
                    try {
                        sessionStorage.setItem(STORAGE_KEY, data.session_id);
                    } catch {}
                }
                setMessages([...newMessages, { role: "assistant", content: data.reply }]);
                setSuggestions(data.suggestions || []);
            } catch (e) {
                setMessages([
                    ...newMessages,
                    {
                        role: "assistant",
                        content:
                            "Désolée, je rencontre un souci de connexion. Réessayez dans quelques secondes ou contactez-nous via le formulaire devis.",
                    },
                ]);
            } finally {
                setSending(false);
            }
        },
        [messages, sessionId, sending]
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(input);
    };

    const handleSuggestion = (s) => {
        if (s.toLowerCase().includes("rappel") || s.toLowerCase().includes("conseiller")) {
            setLeadFormOpen(true);
        } else {
            sendMessage(s);
        }
    };

    const handleLeadSubmit = async (e) => {
        e.preventDefault();
        if (!lead.full_name || !lead.email) return;
        try {
            await api.post("/chatbot/lead", {
                session_id: sessionId || "anonymous",
                full_name: lead.full_name,
                email: lead.email,
                phone: lead.phone || null,
                topic: "Demande de rappel depuis le chatbot Emma",
            });
            setLeadSent(true);
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: `Parfait ${lead.full_name.split(" ")[0]} ! 🎉\n\nVotre demande est transmise à notre équipe. Un conseiller Energy Market vous recontacte sous 24 h ouvrées.\n\nEn attendant, vous pouvez explorer notre [comparateur](/comparateur) ou [demander un devis](/devis).`,
                },
            ]);
            setTimeout(() => {
                setLeadFormOpen(false);
                setLeadSent(false);
            }, 1500);
        } catch {
            // Affiche une erreur dans le chat
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        "Désolée, je n'arrive pas à transmettre votre demande. Pouvez-vous nous joindre via le [formulaire de contact](/contact) ?",
                },
            ]);
            setLeadFormOpen(false);
        }
    };

    const handleClear = () => {
        if (!confirm("Effacer la conversation ?")) return;
        setMessages(WELCOME_MESSAGES);
        setSuggestions(INITIAL_SUGGESTIONS);
        setSessionId(null);
        try {
            sessionStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(STORAGE_HISTORY_KEY);
        } catch {}
    };

    return (
        <>
            {/* Bulle flottante */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    aria-label="Ouvrir le chat Emma"
                    className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[#0A3B2A] text-white shadow-xl shadow-[#0A3B2A]/30 ring-1 ring-white/10 hover:scale-110 hover:bg-[#072719] transition-all flex items-center justify-center"
                >
                    <MessageCircle className="h-6 w-6" strokeWidth={2.2} />
                    {/* Pulse dot pour attirer l'œil */}
                    <span className="absolute -top-1 -right-1 h-3 w-3">
                        <span className="absolute inset-0 rounded-full bg-[#B8E04A] animate-ping opacity-75" />
                        <span className="absolute inset-0 rounded-full bg-[#B8E04A]" />
                    </span>
                </button>
            )}

            {/* Panneau chat */}
            {open && (
                <div className="fixed bottom-6 right-6 z-40 w-[calc(100vw-3rem)] max-w-[400px] h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
                    {/* Header */}
                    <div className="bg-[#0A3B2A] text-white px-5 py-4 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#B8E04A] flex items-center justify-center text-[#0A3B2A] font-bold ring-2 ring-white/10">
                            <Sparkles className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-display font-bold text-base leading-tight">Emma</p>
                            <p className="text-xs text-white/70 leading-tight flex items-center gap-1">
                                <span className="h-1.5 w-1.5 rounded-full bg-[#B8E04A] animate-pulse" />
                                Assistante Energy Market · en ligne
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleClear}
                            title="Effacer la conversation"
                            className="text-white/50 hover:text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded hover:bg-white/10 transition-colors"
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Fermer le chat"
                            className="text-white/70 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-[#F8FBF6] to-white">
                        {messages.map((m, i) => (
                            <MessageBubble key={i} role={m.role} content={m.content} />
                        ))}
                        {sending && (
                            <div className="flex items-end gap-2">
                                <div className="h-7 w-7 rounded-full bg-[#0A3B2A] flex items-center justify-center text-white flex-shrink-0">
                                    <Sparkles className="h-3.5 w-3.5" />
                                </div>
                                <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 ring-1 ring-gray-100">
                                    <TypingDots />
                                </div>
                            </div>
                        )}
                        {/* Boutons suggestions sous la dernière réponse du bot */}
                        {!sending && suggestions.length > 0 && messages[messages.length - 1]?.role === "assistant" && (
                            <div className="flex flex-wrap gap-2 pt-2">
                                {suggestions.map((s, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => handleSuggestion(s)}
                                        className="text-xs px-3 py-1.5 rounded-full bg-white ring-1 ring-[#0A3B2A]/15 text-[#0A3B2A] font-medium hover:bg-[#0A3B2A] hover:text-white transition-colors"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mini-form lead capture */}
                    {leadFormOpen && (
                        <div className="border-t border-gray-100 bg-[#0A3B2A]/5 px-4 py-3">
                            {leadSent ? (
                                <p className="text-sm text-center text-[#0A3B2A] font-medium py-3">
                                    ✓ Demande transmise !
                                </p>
                            ) : (
                                <form onSubmit={handleLeadSubmit} className="space-y-2">
                                    <p className="text-xs font-semibold text-[#0A3B2A] mb-2">
                                        📞 Recevoir un rappel gratuit sous 24 h :
                                    </p>
                                    <input
                                        type="text"
                                        placeholder="Prénom Nom *"
                                        value={lead.full_name}
                                        onChange={(e) => setLead({ ...lead, full_name: e.target.value })}
                                        required
                                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0A3B2A]"
                                    />
                                    <input
                                        type="email"
                                        placeholder="Email *"
                                        value={lead.email}
                                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                                        required
                                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0A3B2A]"
                                    />
                                    <input
                                        type="tel"
                                        placeholder="Téléphone (optionnel)"
                                        value={lead.phone}
                                        onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0A3B2A]"
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setLeadFormOpen(false)}
                                            className="flex-1 px-3 py-2 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            type="submit"
                                            className="flex-1 px-3 py-2 text-xs font-semibold bg-[#0A3B2A] text-white rounded-lg hover:bg-[#072719]"
                                        >
                                            Envoyer
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    )}

                    {/* Input */}
                    {!leadFormOpen && (
                        <form onSubmit={handleSubmit} className="border-t border-gray-100 px-3 py-3 flex items-center gap-2 bg-white">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Écrivez votre message…"
                                disabled={sending}
                                className="flex-1 bg-gray-50 px-4 py-2.5 rounded-full text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0A3B2A]/30"
                            />
                            <button
                                type="submit"
                                disabled={sending || !input.trim()}
                                className="h-10 w-10 rounded-full bg-[#0A3B2A] text-white flex items-center justify-center hover:bg-[#072719] disabled:opacity-30 transition-colors"
                                aria-label="Envoyer"
                            >
                                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            </button>
                        </form>
                    )}
                </div>
            )}
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Sous-composants
// ─────────────────────────────────────────────────────────────────────────
function MessageBubble({ role, content }) {
    const isUser = role === "user";
    // Conversion markdown très basique : [text](url) → <a>, **bold** → <strong>
    // N'autorise que les schémas d'URL sûrs (bloque javascript:, data:, etc.)
    const safeHref = (url) => (/^(https?:|mailto:|tel:)/i.test(url.trim()) ? url.trim() : "#");
    const html = content
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, url) =>
            `<a href="${safeHref(url)}" target="_blank" rel="noopener noreferrer" class="underline text-[#0A3B2A] font-medium">${text}</a>`)
        .replace(/\n/g, "<br/>");
    return (
        <div className={`flex items-end gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
            <div
                className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isUser ? "bg-gray-200 text-gray-600" : "bg-[#0A3B2A] text-white"
                }`}
            >
                {isUser ? <User className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
            </div>
            <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    isUser
                        ? "bg-[#0A3B2A] text-white rounded-br-sm"
                        : "bg-white text-gray-800 ring-1 ring-gray-100 rounded-bl-sm"
                }`}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
}

function TypingDots() {
    return (
        <div className="flex items-center gap-1 py-0.5">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
    );
}
